import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
}                               from 'react-query';
import { Method, AxiosError }   from 'axios';
import { callApi }              from '@utils/apiCaller';
import { Notification }         from '@components/Notification/Notification';
import {
  showErrorMessage,
  parseRecords,
}                               from '@utils/commonFunctions';
import {
  ICreditUnion,
  IDate,
  IReport,
  ICarFaxUser,
  TNotePayload,
  TCarFaxAuthPayload,
  ICuidPayload,
  ILogo,
  IAccountPayload,
  ICUInfo,
  IPartnersInfo,
  ISalesPerson,
  INote,
  IReindeerStatePayload,
  IManager,
  IManagersFacetsStats,
  ISendInvocePayload,
}                               from '@typings/accountManagement';
import {
  ISearchParams,
  ISearchResult,
}                               from '@typings/search';
import { EConnectServiceState } from '@enums/accountManagement';

// #region FETCH ALL ACCOUNTS
const fetchAllAccounts = async (searchStr: string): Promise<ICreditUnion[]> => {
  const payload: ISearchParams = {
    offset     : 0,
    searchText : searchStr || '*',
    orderBy    : 'Name asc',
    filters    : [],
  };

  const { data } = await callApi<ISearchResult<ICreditUnion>>(
    'intraCreditUnion/search',
    'post',
    payload,
  );

  return parseRecords<ICreditUnion>(data);
};

export const useFetchAccounts = (
  searchStr : string,
  config?   : UseQueryOptions<ICreditUnion[], AxiosError>,
) =>
  useQuery(
    ['all-accounts', searchStr],
    () => fetchAllAccounts(searchStr),
    {
      retry                : false,
      refetchOnWindowFocus : false,
      ...config,
    },
  );
// #endregion

// #region FETCH ACCOUNT BY ID
const fetchAccountById = async (payload: string) => {
  const
    { data: cuInfo } = await callApi<ICreditUnion>(`intraCreditUnion/${payload}`);

  const
    { data: cuLogo } = await callApi<ILogo>(`intraCreditUnion/${payload}/logo`);

  const { defaultLocation } = cuInfo;
  let cuData: ICreditUnion = cuInfo;

  if (defaultLocation) {
    const {
      id,
      cuid,
      type,
      locationName,
      ...locationValues
    } = defaultLocation;

    cuData = {
      ...cuData,
      ...locationValues,
    };
  }

  return { cuData, cuLogo };
};

export const useFetchAccountById = (
  payload : string,
  config? : UseQueryOptions<ICUInfo, AxiosError>,
) => useQuery(
  ['fetch-account-by-id', payload],
  () => fetchAccountById(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPLOAD LOGO
interface IUploadLogoPayload {
  cuid     : string;
  logoFile : File;
}

const uploadLogo = async ({ cuid, logoFile }: IUploadLogoPayload): Promise<ILogo> => {
  try {
    const formData = new FormData();

    formData.append('file', logoFile);

    const { data } = await callApi<ILogo>(
      `intraCreditUnion/${cuid}/logo`,
      'post',
      formData,
      { isFormData: true },
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};
// #endregion

// #region UPDATE ACCOUNT
const saveAccount = async (payload: IAccountPayload): Promise<ICreditUnion> => {
  try {
    const { id, cuid, logo: logoFile } = payload;

    const path   : string = id ? `/${cuid}` : '';
    const method : Method = id ? 'put' : 'post';

    const { data } = await callApi<ICreditUnion>(
      `intraCreditUnion${path}`,
      method,
      { ...payload },
    );

    if (logoFile && 'name' in logoFile) {
      await uploadLogo({
        cuid,
        logoFile,
      });
    }

    Notification.enqueueSnackbar(
      'The account was saved.',
      'info',
    );

    return { ...payload, ...data };
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useSaveAccount = (
  config?: UseMutationOptions<ICreditUnion, AxiosError, IAccountPayload>,
) => useMutation(
  saveAccount,
  config,
);
// #endregion

// #region FETCH PARTNERS STATE
const fetchPartnersState = async (cuid: string): Promise<IPartnersInfo> => {
  try {
    const { data = {} as IPartnersInfo } = await callApi(`IntraCreditUnion/connected-services?cuid=${cuid}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchPartnersState = (
  cuid    : string,
  config? : UseQueryOptions<IPartnersInfo, AxiosError>,
) => useQuery(
  ['fetch-partners-info', cuid],
  () => fetchPartnersState(cuid),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH ALL SALESPERSONS
const fetchAllSalesPersons = async (): Promise<ISalesPerson[]> => {
  try {
    const { data = [] as ISalesPerson[] } = await callApi('v2/sales-people');

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchAllSalesPersons = (
  config?: UseQueryOptions<ISalesPerson[]>,
) => useQuery(
  ['fetch-all-sales-persons'],
  fetchAllSalesPersons,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region CREATE OR UPDATE SALESPERSON USER
const handleSaveSalesPerson = async (salesPerson: ISalesPerson): Promise<ISalesPerson> =>  {
  try {
    const { data = {} as ISalesPerson } = salesPerson.id
      ? await callApi(`v2/sales-person?id=${salesPerson.id}`, 'put', salesPerson)
      : await callApi('v2/sales-person', 'post', salesPerson);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSaveSalesPerson = (
  config: UseMutationOptions<ISalesPerson, AxiosError, ISalesPerson> = {},
) => useMutation(
  handleSaveSalesPerson,
  config,
);
// #endregion

// #region GET DATA FOR USER PAGE
const fetchManagers = async (): Promise<ISearchResult<IManager, IManagersFacetsStats>> => {
  try {
    const { data } = await callApi<ISearchResult<IManager, IManagersFacetsStats>>('IntraUsers/managers-search');

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchManagers = (
  config? : UseQueryOptions<ISearchResult<IManager, IManagersFacetsStats>, AxiosError>,
) => useQuery(
  ['fetch-managers'],
  fetchManagers,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region GET DATA WITH FILTER
const fetchManagersByFilter = async (payload: string): Promise<ISearchResult<IManager>> => {
  try {
    const { data } = await callApi<ISearchResult<IManager>>(`IntraUsers/managers-search/${payload}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchManagersByFilter = (
  config?: UseMutationOptions<ISearchResult<IManager>, AxiosError, string>,
) => useMutation(
  fetchManagersByFilter,
  config,
);
// #endregion

// #region FETCH PARTNERS STATE
const fetchNotes = async (payload: string): Promise<INote[]> => {
  try {
    const { data } = await callApi<INote[]>(`IntraNote/notes?cuid=${payload}`);

    return data || [];
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchNotes = (
  payload : string,
  config? : UseQueryOptions<INote[], AxiosError>,
) => useQuery(
  ['fetch-notes', payload],
  () => fetchNotes(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region CREATE NEW NOTE
const createNewNote = async ({ cuid, text }: TNotePayload): Promise<INote[]> => {
  try {
    const { data } = await callApi<INote[]>(
      `IntraNote/notes?cuid=${cuid}&text=${text}`,
      'post',
    );

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useCreateNewNote = (
  config?: UseMutationOptions<INote[], AxiosError, TNotePayload>,
) => useMutation(
  createNewNote,
  config,
);
// #endregion

// #region FETCH SALESPERSONS REPORTS
const fetchReports = async (payload: IDate) => {
  const { year, month } = payload;

  try {
    const { data = [] as IReport[] } = await callApi(`v2/sales-report?year=${year}&month=${month}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchReports = (
  payload : IDate,
  config? : UseQueryOptions<IReport[], AxiosError>,
) => useQuery(
  ['sales-report', payload.year, payload.month],
  () => fetchReports(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH REPORTS DATES
const fetchReportDates = async () => {
  try {
    const { data = [] } = await callApi('v2/report-dates');

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchReportsDates = (
  config? : UseQueryOptions<string[], AxiosError>,
) => useQuery(
  ['fetch-report-dates'],
  fetchReportDates,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region AUTHORIZE TO CARFAX
const authorizeToCarFax = async ({ userName, cuid }: TCarFaxAuthPayload) => {
  try {
    const formData = new FormData();

    formData.append('userName', userName);
    formData.append('cuid', cuid);

    const { data = {} as ICarFaxUser } = await callApi(
      'v2/partners/carfax/report/authorize',
      'post',
      formData,
      { isFormData: true },
    );

    Notification.enqueueSnackbar(
      `Credit Union ${cuid} is authorized to CarFax successfully`,
      'info',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useAuthorizeToCarFax = (
  config: UseMutationOptions<ICarFaxUser, AxiosError, TCarFaxAuthPayload> = {},
) => useMutation(
  authorizeToCarFax,
  config,
);
// #endregion

// #region UNAUTHORIZE FROM CARFAX
const unauthorizeFromCarFax = async ({ cuid }: ICuidPayload) => {
  try {
    await callApi(
      `v2/partners/carfax/report/authorize?cuid=${cuid}`,
      'delete',
    );

    Notification.enqueueSnackbar(
      `Credit Union ${cuid} is unauthorized from CarFax successfully`,
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );
  }
};

export const useUnauthorizeFromCarFax = (
  config: UseMutationOptions<void, AxiosError, ICuidPayload> = {},
) => useMutation(
  unauthorizeFromCarFax,
  config,
);
// #endregion

// #region FETCH NEXT CUID
const fetchNextCuid = async (): Promise<string> => {
  try {
    const { data } = await callApi<ICuidPayload>('intraCreditUnion/next-cuid');

    return data.cuid;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchNextCuid = (
  config?: UseQueryOptions<string, AxiosError>,
) => useQuery(
  ['next-cuid'],
  fetchNextCuid,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH REINDEER STATE
const fetchReindeerState = async (cuid: string): Promise<EConnectServiceState> => {
  try {
    const { data } = await callApi<EConnectServiceState>(
      'v2/partners/reindeer/state',
      'get',
      { cuid },
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchReindeerState = (
  cuid    : string,
  config? : UseQueryOptions<EConnectServiceState, AxiosError>,
) => useQuery(
  ['reindeer-state', cuid],
  () => fetchReindeerState(cuid),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPDATE REINDEER STATE
const updateReindeerState = async ({ cuid, isAuth }: IReindeerStatePayload): Promise<void> => {
  try {
    const method        : Method = isAuth ? 'patch' : 'delete';
    const messageAction : string = isAuth ? 'authorized to' : 'unauthorized from';

    await callApi(
      `v2/partners/reindeer/authorize?cuid=${cuid}`,
      method,
    );

    Notification.enqueueSnackbar(
      `Credit Union ${cuid} is ${messageAction} Reindeer successfully.`,
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdateReindeerState = (
  config: UseMutationOptions<void, AxiosError, IReindeerStatePayload> = {},
) => useMutation(
  updateReindeerState,
  config,
);
// #enderegion

// #region FETCH INVOICE EMAILS
const fetchInvoiceEmails = async (cuid?: string): Promise<string[]> => {
  try {
    const { data } = await callApi<string[]>(
      'intraCreditUnion/invoice-emails',
      'get',
      { cuid },
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInvoiceEmails = (
  cuid?   : string,
  config? : UseQueryOptions<string[], AxiosError>,
) => useQuery(
  ['invoice-emails', cuid],
  () => fetchInvoiceEmails(cuid),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region SEND INVOICE EMAILS
const sendInvoiceEmails = async (payload: ISendInvocePayload): Promise<void> => {
  const { cuid, invoiceId, emails } = payload;

  try {
    await callApi<string[]>(
      `IntraInvoice/send-invoice-to-emails/${cuid}/${invoiceId}`,
      'post',
      { emails },
    );

    Notification.enqueueSnackbar(
      'Invoices have been sent.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSendInvoiceEmails = (
  config: UseMutationOptions<void, AxiosError, ISendInvocePayload> = {},
): UseMutationResult<void, AxiosError, ISendInvocePayload> => useMutation(
  sendInvoiceEmails,
  config,
);
// #endregion
